import Card from "components/card";
import Loader from "components/loader";
import { getInstituciones } from "services/Reports";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { routesNames } from "utils/Constants";
import { DASHBOARD_COLORS } from "utils/dashboardColors";

const round = (value) => (Number.isFinite(value) ? Math.round(value * 10) / 10 : 0);

const buildPlanStats = (rows) => {
  if (!rows.length) {
    return { count: 0, avgPublicaciones: 0, avgPostulaciones: 0, conActivas: 0, sinPublicaciones: 0 };
  }

  const totalPublicaciones = rows.reduce((acc, item) => acc + Number(item?.publicaciones_count || 0), 0);
  const totalPostulaciones = rows.reduce((acc, item) => acc + Number(item?.postulaciones_count || 0), 0);
  const conActivas = rows.filter((item) => Number(item?.publicaciones_activas_count || 0) > 0).length;
  const sinPublicaciones = rows.filter((item) => Number(item?.publicaciones_count || 0) === 0).length;

  return {
    count: rows.length,
    avgPublicaciones: round(totalPublicaciones / rows.length),
    avgPostulaciones: round(totalPostulaciones / rows.length),
    conActivas,
    sinPublicaciones,
  };
};

const PlanStatBlock = ({ title, accent, stats }) => (
  <div className="flex-1 rounded-xl border border-gray-100 p-4 dark:border-navy-700">
    <p className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>
      {title} ({stats.count})
    </p>
    <div className="mt-3 grid grid-cols-2 gap-3">
      <div>
        <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.avgPublicaciones}</p>
        <p className="text-xs text-gray-600">publicaciones prom.</p>
      </div>
      <div>
        <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.avgPostulaciones}</p>
        <p className="text-xs text-gray-600">postulaciones prom.</p>
      </div>
      <div>
        <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.conActivas}</p>
        <p className="text-xs text-gray-600">con publicaciones activas</p>
      </div>
      <div>
        <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.sinPublicaciones}</p>
        <p className="text-xs text-gray-600">sin ninguna publicación</p>
      </div>
    </div>
  </div>
);

const InstitutionsComparisonWidget = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [instituciones, setInstituciones] = useState([]);

  const fetchInstituciones = async () => {
    setIsLoading(true);
    const data = await getInstituciones();
    const rows = Array.isArray(data?.[0]) ? data[0] : [];
    setInstituciones(rows);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchInstituciones();
  }, []);

  const goldStats = useMemo(
    () => buildPlanStats(instituciones.filter((item) => item?.user?.plan?.detalle?.nombre === "Gold")),
    [instituciones]
  );
  const standardStats = useMemo(
    () => buildPlanStats(instituciones.filter((item) => item?.user?.plan?.detalle?.nombre !== "Gold")),
    [instituciones]
  );

  const dormidas = goldStats.sinPublicaciones + standardStats.sinPublicaciones;

  return (
    <Card extra="w-full h-full px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Gold vs Standard
        </div>
        <Link
          to={routesNames.INSTITUCIONES}
          className="text-sm hover:cursor-pointer bg-lightPrimary p-2 text-navy-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 linear justify-center rounded-lg font-semibold transition duration-200"
        >
          Ver instituciones
        </Link>
      </div>

      {isLoading ? (
        <div className="mt-4 flex h-[220px] items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <PlanStatBlock title="Gold" accent={DASHBOARD_COLORS.gold} stats={goldStats} />
            <PlanStatBlock title="Standard" accent={DASHBOARD_COLORS.otros} stats={standardStats} />
          </div>

          {dormidas > 0 && (
            <div
              className="mt-4 flex items-start gap-2 rounded-lg p-2 text-xs text-gray-700 dark:text-gray-300"
              style={{ backgroundColor: `${DASHBOARD_COLORS.alerta}1A` }}
            >
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: DASHBOARD_COLORS.alerta }} />
              <p>
                <span className="font-bold text-navy-700 dark:text-white">{dormidas}</span> instituciones
                nunca publicaron nada ({standardStats.sinPublicaciones} Standard · {goldStats.sinPublicaciones} Gold).
              </p>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default InstitutionsComparisonWidget;
